// Error tracking: Sentry SDK → GlitchTip (docs/project.md §12). Everything that says something
// about our code goes to the tracker. An ApiRequestError does not: it is the network's or the API's
// answer, the user already sees it as a plain message, and the API reports its own faults.
import * as Sentry from '@sentry/react';
import { ApiRequestError, ApiResponseError } from '@/api/client';
import { APP_RELEASE, SENTRY_DSN } from './env';

export function initErrorTracking(): void {
  if (!SENTRY_DSN) return;
  Sentry.init({
    dsn: SENTRY_DSN,
    release: APP_RELEASE,
    beforeSend(event, hint) {
      const e = hint.originalException;
      if (isExpected(e)) return null;
      if (e instanceof ApiResponseError) {
        event.tags = { ...event.tags, api: scrubUrl(e.path) };
        event.extra = { ...event.extra, issues: e.issues };
      }
      const request = event.request;
      if (request?.url) request.url = scrubUrl(request.url);
      if (request?.headers?.Referer) request.headers.Referer = scrubUrl(request.headers.Referer);
      return event;
    },
    beforeBreadcrumb(crumb) {
      if (!crumb.data) return crumb;
      const data: Record<string, unknown> = { ...crumb.data };
      for (const key of ['url', 'from', 'to']) {
        const value = data[key];
        if (typeof value === 'string') data[key] = scrubUrl(value);
      }
      for (const key of ['url.query', 'url.fragment', 'http.query', 'http.fragment']) delete data[key];
      return { ...crumb, data };
    },
  });
}

// `tags` are for the tracker's grouping and filters; they carry no personal data either
export function reportError(e: unknown, tags?: Record<string, string>): void {
  Sentry.captureException(e, tags ? { tags } : undefined);
}

// For an error that repeats on every render (a missing translation): one event per page load.
const reported = new Set<string>();
export function reportOnce(e: Error): void {
  if (reported.has(e.message)) return;
  reported.add(e.message);
  reportError(e);
}

function isExpected(e: unknown): boolean {
  if (e instanceof DOMException && e.name === 'AbortError') return true;
  return e instanceof ApiRequestError && !(e instanceof ApiResponseError);
}

// Paths and queries may carry a dictionary word (/d/…/l/{headword}, ?headword=) or a share token
// (/share/{token}); the tracker gets the route shape only (docs/project.md §12).
const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
export function scrubUrl(url: string): string {
  return url
    .replace(/[?#].*$/, '')
    .replace(UUID, ':id')
    .replace(/\/(l|share|shares)\/[^/]+/g, (_, segment: string) =>
      segment === 'l' ? '/l/:headword' : `/${segment}/:token`,
    );
}
